import type { TextualSearchRequest } from 'src/components/searchbox'

import * as L from 'leaflet'

import md5 from 'md5'
import { CubePresenter, Presenter, FlipIntent, Logger, NOOP_PROMISE_VOID } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'
import { SearchBoxPresenter } from 'src/components/searchbox'

import { TheMapKeys } from './tm_keys'
import { TheMapMarkDetailKeys } from './mark_details'
import {
    TheMapScope,
    TheMapHeaderScope,
    TheMapSelectionBarScope,
    TheMapSelectionScope,
    ScopeDefaults
} from './tm_scopes'

import TheMapService, { type PlotItem } from './tm_service'

const LOG = Logger.get('TheMapPresenter')

const service = TheMapService.singleton()
const MARK_CIRCLE_CLASSES = buildMarkCircleClasses()

const ESPERA_DE_MOVIMENTO_MS = 400

type SelectionCtx = {
    selection: TheMapSelectionScope
    layerGroup: L.LayerGroup
    request: TextualSearchRequest
    markClassName: string
    ordem: number
    esgotadas: L.LatLngBounds[]
    refinando: boolean
    pendente: boolean
    marcadores: Map<string, L.Marker>
    ultimoRecorte: string
}

function paraBounds(visivel: L.LatLngBounds) {
    const oeste = visivel.getWest()
    const leste = visivel.getEast()
    const voltaInteira = leste - oeste >= 360

    const normal = (lon: number) => ((((lon + 180) % 360) + 360) % 360) - 180

    return {
        top_left: { lat: visivel.getNorth(), lon: voltaInteira ? -180 : normal(oeste) },
        bottom_right: { lat: visivel.getSouth(), lon: voltaInteira ? 180 : normal(leste) }
    }
}

export class TheMapPresenter extends CubePresenter<MainPresenter, TheMapScope> {
    #headerBar: Presenter<TheMapHeaderScope, TheMapPresenter>
    #selectionBar: Presenter<TheMapSelectionBarScope, TheMapPresenter>
    #searchPresenter: SearchBoxPresenter
    #map: L.Map | null = null
    #requestGroupId?: string
    #contextos: SelectionCtx[] = []
    #temporizadorDeMovimento?: ReturnType<typeof setTimeout>
    #pendingRequesGrouptUpdate = NOOP_PROMISE_VOID

    public constructor(app: MainPresenter) {
        super(app, new TheMapScope())
        this.#headerBar = new Presenter(this, new TheMapHeaderScope())
        this.#selectionBar = new Presenter(this, new TheMapSelectionBarScope())
        this.#searchPresenter = new SearchBoxPresenter(app, this, this.#headerBar.scope.search)
        this.#searchPresenter.mode = 'MAP'
        this.#searchPresenter.paginacao = true
    }

    public override release() {
        if (this.app.scope.toolbar === this.#headerBar.scope) {
            this.app.scope.toolbar = undefined
        }

        if (this.#temporizadorDeMovimento) {
            clearTimeout(this.#temporizadorDeMovimento)
            this.#temporizadorDeMovimento = undefined
        }
        this.#map?.off('moveend', this.#handleMapMoved)

        this.#searchPresenter.release()
        this.#selectionBar.release()
        this.#headerBar.release()

        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        if (!this.app.scope.theMapEnabled) {
            await this.app.flipToDefault()
            return false
        }

        const keys = new TheMapKeys(this.app, intent)

        this.app.scope.toolbar = this.#headerBar.scope
        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize(keys)
        } else if (keys.requestGroupId !== this.#requestGroupId) {
            await this.#loadRequests(keys.requestGroupId)
        }

        if (keys.last) {
            keys.dialogSlot(null)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new TheMapKeys(this.app, intent)
        keys.requestGroupId = this.#requestGroupId
    }

    async #initialize(keys: TheMapKeys) {
        this.app.scope.toolbar = this.#headerBar.scope
        this.scope.selectionBar = this.#selectionBar.scope

        this.scope.onMapChanged = this.action(this.#handleMapChanged)

        this.#headerBar.scope.onOnlyActivesChange = this.action(this.#handleOnlyActivesChange)
        this.#headerBar.scope.onSomenteComGeoChange = this.action(this.#handleSomenteComGeoChange)

        this.#searchPresenter.initialize()
        this.#searchPresenter.onComplianceChanged(keys.compliance)
        this.#searchPresenter.onApply = this.#handleApplyFilter.bind(this)
        this.#pendingRequesGrouptUpdate = async () => {
            this.#pendingRequesGrouptUpdate = NOOP_PROMISE_VOID
            await this.#loadRequests(keys.requestGroupId)
        }
    }

    async #loadRequests(requestGroupId: string | undefined | null) {
        this.#selectionBar.scope.selections.length = 0
        this.#contextos.length = 0
        this.#requestGroupId = undefined

        const requestGroupJson = requestGroupId ? localStorage.getItem(requestGroupId) : null
        if (requestGroupId && requestGroupJson) {
            this.#requestGroupId = requestGroupId
            const requests = JSON.parse(requestGroupJson) as TextualSearchRequest[]
            for (const request of requests) {
                await this.#handleApplyFilter(request)
            }
        }
    }

    // :: Events

    public onComplianceChanged(compliance: boolean) {
        this.#searchPresenter.onComplianceChanged(compliance)
    }

    async #handleMapChanged(map: L.Map | null) {
        this.#map = map
        if (map) {
            map.setView(ScopeDefaults.getDefaultCenter(), 4)
            map.on('moveend', this.#handleMapMoved)
            this.#pendingRequesGrouptUpdate()
        }
    }

    #handleMapMoved = () => {
        if (this.#temporizadorDeMovimento) {
            clearTimeout(this.#temporizadorDeMovimento)
        }
        this.#temporizadorDeMovimento = setTimeout(() => {
            this.#temporizadorDeMovimento = undefined
            this.#refinarPelaTela().catch((causa) => LOG.error('refinarPelaTela', causa))
        }, ESPERA_DE_MOVIMENTO_MS)
    }

    async #refinarPelaTela() {
        const leafletMap = this.#map
        if (!leafletMap || this.#contextos.length === 0) {
            return
        }

        const visivel = leafletMap.getBounds()
        await Promise.all(this.#contextos.map((ctx) => this.#refinarSelecao(ctx, visivel)))
    }

    async #refinarSelecao(ctx: SelectionCtx, visivel: L.LatLngBounds) {
        const leafletMap = this.#map
        if (!leafletMap || !ctx.selection.checked) {
            return
        }

        if (ctx.refinando) {
            ctx.pendente = true
            return
        }

        if (ctx.esgotadas.some((esgotada) => esgotada.contains(visivel))) {
            return
        }

        const bounds = paraBounds(visivel)
        const recorte = JSON.stringify(bounds)
        if (recorte === ctx.ultimoRecorte) {
            return
        }

        ctx.refinando = true
        try {
            const data = await service.fetch({ ...ctx.request, bounds })

            // O mapa pode ter sido trocado ou a selecao removida durante a consulta.
            if (this.#map !== leafletMap || !this.#contextos.includes(ctx)) {
                return
            }

            if (data.exaustivo) {
                this.#registrarEsgotada(ctx, visivel)
            }

            this.#reconciliarCamada(ctx, data.scatter)

            ctx.ultimoRecorte = recorte
        } finally {
            ctx.refinando = false
        }

        if (ctx.pendente) {
            ctx.pendente = false
            const agora = this.#map
            if (agora && this.#contextos.includes(ctx)) {
                await this.#refinarSelecao(ctx, agora.getBounds())
            }
        }
    }

    #registrarEsgotada(ctx: SelectionCtx, novo: L.LatLngBounds) {
        if (ctx.esgotadas.some((esgotada) => esgotada.contains(novo))) {
            return
        }
        ctx.esgotadas = ctx.esgotadas.filter((esgotada) => !novo.contains(esgotada))
        ctx.esgotadas.push(novo)
    }

    async #handleOnlyActivesChange(checked: boolean) {
        this.#headerBar.scope.onlyActives = checked
        this.#searchPresenter.showInactives = !checked
    }

    async #handleSomenteComGeoChange(checked: boolean) {
        this.#headerBar.scope.somenteComGeo = checked
        this.#searchPresenter.somenteComGeo = checked
    }

    async #handleApplyFilter(request: TextualSearchRequest) {
        LOG.debug('#handleApplyFilter:', request)
        const leafletMap = this.#map
        if (!leafletMap) {
            return
        }

        const data = await service.fetch(request)

        const markClassName =
            MARK_CIRCLE_CLASSES[this.#selectionBar.scope.selections.length % MARK_CIRCLE_CLASSES.length]

        const ordem = this.#selectionBar.scope.selections.length
        const { layerGroup, marcadores, southWest, northEast } = this.#montarCamada(
            request,
            markClassName,
            ordem,
            data.scatter
        )
        const center = ScopeDefaults.getDefaultCenter()

        layerGroup.addTo(leafletMap)

        const selection = new TheMapSelectionScope()
        selection.request = request
        selection.title = await this.#buildSelectionTitle(request)
        selection.mark = markClassName
        selection.checked = true

        selection.update = this.#selectionBar.update
        {
            const ctx: SelectionCtx = {
                selection,
                layerGroup,
                request,
                markClassName,
                ordem,
                esgotadas: [],
                refinando: false,
                pendente: false,
                marcadores,
                // A primeira consulta nao tem recorte, entao qualquer movimento seguinte
                // difere dela e a navegacao comeca valendo.
                ultimoRecorte: ''
            }
            if (data.exaustivo) {
                ctx.esgotadas.push(new L.LatLngBounds({ lat: -90, lng: -180 }, { lat: 90, lng: 180 }))
            }
            selection.onCheckChange = this.action(this.#handleSelectionCheckChange.bind(this, ctx))
            selection.onDelete = this.action(this.#handleSelectionDelete.bind(this, ctx))
            this.#contextos.push(ctx)
        }

        this.#selectionBar.scope.selections.push(selection)

        this.scope.selectionBar = this.#selectionBar.scope

        if (data.scatter.length === 1) {
            const item = data.scatter[0]
            center.lat = item.value[1]
            center.lng = item.value[0]
            leafletMap.flyTo(center, 13)
        } else if (data.scatter.length > 1) {
            leafletMap.flyToBounds(new L.LatLngBounds(southWest, northEast), { maxZoom: 13 })
        } else {
            leafletMap.flyTo(center, 4)
        }

        this.#updateRequestCache()
    }

    #montarCamada(request: TextualSearchRequest, markClassName: string, ordem: number, scatter: PlotItem[]) {
        const layerGroup = L.layerGroup()
        const marcadores = new Map<string, L.Marker>()

        const center = ScopeDefaults.getDefaultCenter()
        const southWest: L.LatLngLiteral = { ...center }
        const northEast: L.LatLngLiteral = { ...center }

        // For Each Scatter
        if (scatter.length > 0) {
            const scatterGroupMap = new Map<string, PlotItem[]>()
            for (const item of scatter) {
                const key = `${item.value[0]}:${item.value[1]}`
                let scatterGroup = scatterGroupMap.get(key)
                if (!scatterGroup) {
                    scatterGroup = []
                    scatterGroupMap.set(key, scatterGroup)
                }
                scatterGroup.push(item)
            }

            const scatterIt = scatterGroupMap.values()[Symbol.iterator]()
            let entry = scatterIt.next()
            if (!entry.done) {
                const someEntry = entry.value[0]
                northEast.lng = southWest.lng = someEntry.value[0]
                northEast.lat = southWest.lat = someEntry.value[1]
            }

            while (!entry.done) {
                const someEntry = entry.value[0]
                const lng = someEntry.value[0]
                const lat = someEntry.value[1]

                southWest.lng = Math.min(southWest.lng, lng)
                northEast.lng = Math.max(northEast.lng, lng)

                northEast.lat = Math.min(northEast.lat, lat)
                southWest.lat = Math.max(southWest.lat, lat)

                const names = entry.value.map((e) => e.name)
                const marker = new L.Marker(
                    { lng, lat },
                    {
                        title: names.join('\n'),
                        zIndexOffset: ordem,
                        icon: request.empresa
                            ? L.divIcon({ className: markClassName, iconSize: L.point(12, 12) })
                            : L.divIcon({ className: 'gg-pin', iconSize: L.point(24, 24) })
                    }
                )
                marker.on('click', this.#handleMarkerClicked.bind(this, request, entry.value))
                layerGroup.addLayer(marker)
                marcadores.set(`${lng}:${lat}`, marker)

                entry = scatterIt.next()
            }
        }

        return { layerGroup, marcadores, southWest, northEast }
    }

    #reconciliarCamada(ctx: SelectionCtx, scatter: PlotItem[]) {
        const novos = new Map<string, PlotItem[]>()
        for (const item of scatter) {
            const chave = `${item.value[0]}:${item.value[1]}`
            const grupo = novos.get(chave)
            if (grupo) {
                grupo.push(item)
            } else {
                novos.set(chave, [item])
            }
        }

        const aRemover: [string, L.Marker][] = []
        for (const [chave, marker] of ctx.marcadores) {
            if (!novos.has(chave)) {
                aRemover.push([chave, marker])
            }
        }

        const aCriar: [string, PlotItem[]][] = []
        let atualizados = 0
        for (const [chave, itens] of novos) {
            const existente = ctx.marcadores.get(chave)
            if (!existente) {
                aCriar.push([chave, itens])
                continue
            }

            const titulo = itens.map((e) => e.name).join('\n')
            if (existente.options.title !== titulo) {
                existente.options.title = titulo
                const elemento = existente.getElement()
                if (elemento) {
                    elemento.title = titulo
                }
                existente.off('click')
                existente.on('click', this.#handleMarkerClicked.bind(this, ctx.request, itens))
                atualizados++
            }
        }

        LOG.debug(
            `#reconciliarCamada: ${novos.size} pontos` + ` (+${aCriar.length} -${aRemover.length} ~${atualizados})`
        )

        if (aRemover.length === 0 && aCriar.length === 0) {
            return
        }

        for (const [chave, marker] of aRemover) {
            ctx.layerGroup.removeLayer(marker)
            ctx.marcadores.delete(chave)
        }
        for (const [chave, itens] of aCriar) {
            const marker = this.#criarMarcador(ctx, chave, itens)
            ctx.layerGroup.addLayer(marker)
            ctx.marcadores.set(chave, marker)
        }
    }

    /** Um marcador da selecao, com a cor e o desempate de empilhamento dela. */
    #criarMarcador(ctx: SelectionCtx, chave: string, itens: PlotItem[]) {
        const [lng, lat] = chave.split(':').map(Number)
        const marker = new L.Marker(
            { lng, lat },
            {
                title: itens.map((e) => e.name).join('\n'),
                zIndexOffset: ctx.ordem,
                icon: ctx.request.empresa
                    ? L.divIcon({ className: ctx.markClassName, iconSize: L.point(12, 12) })
                    : L.divIcon({ className: 'gg-pin', iconSize: L.point(24, 24) })
            }
        )
        marker.on('click', this.#handleMarkerClicked.bind(this, ctx.request, itens))
        return marker
    }

    async #buildSelectionTitle(req: TextualSearchRequest) {
        const labels = [] as string[]
        await this.#searchPresenter.describeRequest(req, labels)
        return labels.join('; ')
    }

    #updateRequestCache() {
        const requestArray = this.#selectionBar.scope.selections.filter((e) => !!e.request).map((e) => e.request)

        if (requestArray.length > 0) {
            const requestGroupJson = JSON.stringify(requestArray)
            this.#requestGroupId = md5(requestGroupJson)
            localStorage.setItem(this.#requestGroupId, requestGroupJson)
        } else {
            this.#requestGroupId = undefined
        }

        this.updateHistory()
    }

    async #handleSelectionCheckChange(ctx: SelectionCtx, checked: boolean) {
        LOG.debug(`handleSelectionCheckChange(checked=${checked})`)
        ctx.selection.checked = checked
        if (checked) {
            this.#map && ctx.layerGroup.addTo(this.#map)
        } else {
            ctx.layerGroup.remove()
        }
    }

    async #handleSelectionDelete(ctx: SelectionCtx) {
        ctx.selection.checked = false
        ctx.layerGroup.remove()
        this.#contextos = this.#contextos.filter((e) => e !== ctx)
        this.#selectionBar.scope.selections.removeByCriteria((v) => ctx.selection.uid === v.uid)
        this.#selectionBar.scope.update()

        this.#updateRequestCache()
    }

    async #handleMarkerClicked(request: TextualSearchRequest, plotItens: PlotItem[], evt: L.LeafletMouseEvent) {
        this.scope.onMarkCopyDataToClipboard = this.action(async () => {
            const names = plotItens.map((e) => e.name)
            await navigator.clipboard.writeText(names.join('\n'))
        })

        this.scope.onMarkShowDetailsClipboard = this.action(async () => {
            const requestJson = JSON.stringify(request)
            const requestId = md5(requestJson)
            localStorage.setItem(requestId, requestJson)

            const target = new TheMapMarkDetailKeys(this.app)
            target.requestId = requestId
            await target.flip()
        })

        this.scope.showMarkMenu(evt.originalEvent)
    }
}

function buildMarkCircleClasses() {
    return [
        'gg-ue-red-circle',
        'gg-islamic-green-circle',
        'gg-duke-blue-circle',
        'gg-mughal-green-circle',
        'gg-weldon-blue-circle'
    ]
}
